<script setup>
import { useForm } from '@inertiajs/vue3'
import { computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import { DEFAULT_PLATFORMS } from '@/composables/useChannelConfig.js'
import { NATIONALITIES } from '@/composables/useNationalities.js'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

const props = defineProps({
  source:   { type: Object, required: true },
  channels: { type: Array,  default: () => [] },
  errors:   { type: Object, default: () => ({}) },
  defaults: { type: Object, default: () => ({}) }
})

const { t } = useI18n()
const isEdit = computed(() => !!props.source.id)

function seedChannels(channels) {
  const result = [...channels]
  for (const platform of DEFAULT_PLATFORMS) {
    if (!result.find(c => c.platform === platform)) {
      result.push({ platform, handle: '', custom_label: '', position: DEFAULT_PLATFORMS.indexOf(platform) })
    }
  }
  return result
}

const form = useForm({
  name:                         props.source.name ?? '',
  first_name:                   props.source.first_name ?? '',
  last_name:                    props.source.last_name ?? '',
  contact_phone:                props.source.contact_phone ?? '',
  contact_email:                props.source.contact_email ?? '',
  date_of_birth:                props.source.date_of_birth ?? '',
  nationality:                  props.source.nationality ?? '',
  passport_no:                  props.source.passport_no ?? '',
  licence_number:               props.source.licence_number ?? '',
  licence_expiry:               props.source.licence_expiry ?? '',
  address:                      props.source.address ?? '',
  notes:                        props.source.notes ?? '',
  licence_scan:                 null,
  passport_photo:               null,
  contact_channels_attributes:  seedChannels(props.channels),
  return_to:                    props.defaults?.return_to ?? '',
  link_conversation:            props.defaults?.link_conversation ?? ''
})

function submit() {
  form.transform((data) => {
    const { return_to, link_conversation, ...sourceData } = data
    return {
      vehicle_source: {
        ...sourceData,
        contact_channels_attributes: sourceData.contact_channels_attributes
          .filter(c => c.handle !== '' || c._destroy)
          .map((c, i) => ({ ...c, position: i }))
      },
      return_to,
      link_conversation
    }
  })
  const opts = { preserveScroll: true, forceFormData: true }
  if (isEdit.value) {
    form.patch(`/vehicle_sources/${props.source.id}`, opts)
  } else {
    form.post('/vehicle_sources', opts)
  }
}

const err = (k) => form.errors[k] || (props.errors[k] && props.errors[k][0])

const inputBase = 'w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]'
const labelCls  = 'block text-sm font-medium text-[color:var(--inkSoft)] mb-1'
</script>

<template>
  <PageShell
    narrow
    :title="isEdit ? t('suppliers.form.edit') : t('suppliers.form.new')"
    back-href="/vehicle_sources"
    :back-label="t('common.back')"
  >
    <form
      id="supplier-form"
      class="space-y-4"
      @submit.prevent="submit"
    >
      <div>
        <label :class="labelCls">{{ t('suppliers.form.name') }} *</label>
        <UiInput
          v-model="form.name"
          required
          :error="err('name')"
        />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">First Name</label>
          <input
            v-model="form.first_name"
            type="text"
            :class="[inputBase, err('first_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
          />
          <p v-if="err('first_name')" class="mt-1 text-xs text-[color:var(--danger)]">{{ err('first_name') }}</p>
        </div>
        <div>
          <label :class="labelCls">Last Name</label>
          <input
            v-model="form.last_name"
            type="text"
            :class="[inputBase, err('last_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]']"
          />
          <p v-if="err('last_name')" class="mt-1 text-xs text-[color:var(--danger)]">{{ err('last_name') }}</p>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">{{ t('suppliers.form.phone') }}</label>
          <PhoneInput v-model="form.contact_phone" />
        </div>
        <div>
          <label :class="labelCls">{{ t('suppliers.form.email') }}</label>
          <input
            v-model="form.contact_email"
            type="email"
            :class="[inputBase, 'border-[color:var(--line)]']"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">Date of Birth</label>
          <input
            v-model="form.date_of_birth"
            type="date"
            :class="[inputBase, 'border-[color:var(--line)]']"
          />
        </div>
        <div>
          <label :class="labelCls">Nationality</label>
          <select
            v-model="form.nationality"
            :class="[inputBase, 'border-[color:var(--line)]']"
          >
            <option value="">—</option>
            <option v-for="n in NATIONALITIES" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">Passport Number</label>
          <input
            v-model="form.passport_no"
            type="text"
            :class="[inputBase, 'border-[color:var(--line)]']"
          />
        </div>
        <div>
          <label :class="labelCls">Licence Number</label>
          <input
            v-model="form.licence_number"
            type="text"
            :class="[inputBase, 'border-[color:var(--line)]']"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">Licence Expiry</label>
          <input
            v-model="form.licence_expiry"
            type="date"
            :class="[inputBase, 'border-[color:var(--line)]']"
          />
        </div>
        <div />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label :class="labelCls">Licence Scan</label>
          <input
            type="file"
            accept="image/*,.pdf"
            class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
            @change="form.licence_scan = $event.target.files[0]"
          />
          <a
            v-if="props.source.licence_scan_url"
            :href="props.source.licence_scan_url"
            target="_blank"
            class="mt-1 inline-block text-xs underline text-[color:var(--accent)]"
          >View current</a>
        </div>
        <div>
          <label :class="labelCls">Passport Photo</label>
          <input
            type="file"
            accept="image/*,.pdf"
            class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
            @change="form.passport_photo = $event.target.files[0]"
          />
          <a
            v-if="props.source.passport_photo_url"
            :href="props.source.passport_photo_url"
            target="_blank"
            class="mt-1 inline-block text-xs underline text-[color:var(--accent)]"
          >View current</a>
        </div>
      </div>

      <div>
        <label :class="labelCls">Address</label>
        <UiTextarea
          v-model="form.address"
          :rows="2"
        />
      </div>

      <div>
        <label :class="labelCls">{{ t('suppliers.form.notes') }}</label>
        <UiTextarea
          v-model="form.notes"
          :rows="3"
          :error="err('notes')"
        />
      </div>

      <div class="border-t border-[color:var(--line)] pt-4">
        <label :class="labelCls">
          {{ t('suppliers.form.channels') }}
        </label>
        <CustomerChannels v-model="form.contact_channels_attributes" />
      </div>
    </form>

    <StickyActionBar>
      <UiButton
        type="submit"
        form="supplier-form"
        variant="primary"
        :loading="form.processing"
      >
        {{ isEdit ? t('suppliers.form.save') : t('suppliers.form.create') }}
      </UiButton>
    </StickyActionBar>
  </PageShell>
</template>
